<template>
    <lb-dialog data-testid="ai-info-table__dialog" v-model="internalShow"
        :heading="dialogHeading" :width="dialogWidth"
        :loading="loading" persistent>
        <template v-slot:body>
            <v-container data-testid="ai-info-table__dialog-container" fluid class="pa-0">
                <!-- Header Section -->
                <v-card data-testid="ai-info-table__header-card" outlined class="mb-4" v-if="editable">
                    <v-card-text data-testid="ai-info-table__header-text" class="pa-4">
                        <v-row class="align-center">
                            <v-col cols="12" sm="3" class="font-weight-medium primary--text">Name:</v-col>
                            <v-col cols="12" sm="9" class="text-body-1"
                                data-testid="ai-info-table__feature-name">{{ dialogData.name }}</v-col>

                            <v-col cols="12" sm="3" class="font-weight-medium primary--text">Model:</v-col>
                            <v-col cols="12" sm="9">
                                <v-chip data-testid="ai-info-table__dialog-model-chip" class="ma-1" color="primary"
                                    small label outlined>
                                    <v-icon data-testid="ai-info-table__dialog-model-icon" left
                                        small>mdi-cube-outline</v-icon>
                                    <span>{{ dialogData.model }}</span>
                                </v-chip>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <!-- Template Information -->
                <v-card data-testid="ai-info-table__template-card" outlined class="mb-4">
                    <v-card-text data-testid="ai-info-table__template-text" class="pa-3">
                        <v-row class="align-center">
                            <v-col cols="12" sm="3" class="font-weight-medium primary--text">
                                <span class="font-weight-regular">Feature Name</span>
                            </v-col>
                            <v-col cols="12" sm="9">
                                <p data-testid="ai-info-table__template-description" class="font-weight-semibold">
                                    {{ dialogData.feature_name }}</p>
                            </v-col>

                            <v-col cols="12" sm="3" class="font-weight-medium primary--text">
                                <span class="font-weight-regular">Template description</span>
                            </v-col>
                            <v-col cols="12" sm="9">
                                <p data-testid="ai-info-table__template-description" class="font-weight-semibold">
                                    {{ dialogData.description }}</p>
                            </v-col>

                            <v-col cols="12" sm="3" class="font-weight-medium primary--text"
                                v-if="dialogMode !== POP_MODEL.EDITOR">
                                <span class="font-weight-regular">Template Name</span>
                            </v-col>
                            <v-col cols="12" sm="9" v-if="dialogMode !== POP_MODEL.EDITOR">
                                <template
                                    v-if="filteredItems && Array.isArray(filteredItems) && filteredItems.length">
                                    <v-chip-group data-testid="ai-info-table__template-variants-group" column>
                                        <v-chip data-testid="ai-info-table__template-variant-chip"
                                            v-for="(variant, i) in filteredItems" :key="i" small class="ma-1">
                                            <v-icon data-testid="ai-info-table__template-variant-icon" left
                                                small>mdi-text-box-outline</v-icon>
                                            <span>{{ variant.type }}</span>
                                        </v-chip>
                                    </v-chip-group>
                                </template>
                                <template v-else>
                                    <v-chip data-testid="ai-info-table__no-type-chip" small color="grey lighten-2">
                                        <span>{{ dialogData.type || 'No type available' }}</span>
                                    </v-chip>
                                </template>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <!-- Admin Permissions -->
                <v-card data-testid="ai-info-table__admin-permissions-card" outlined class="mb-4">
                    <v-card-title data-testid="ai-info-table__admin-permissions-title" class="pa-3">
                        <h5 class="font-weight-semibold">Permissions</h5>
                    </v-card-title>
                    <v-card-text data-testid="ai-info-table__admin-permissions-text" class="pa-3">
                        <v-row class="align-center">
                            <v-col cols="12"
                                class="d-flex align-center justify-space-between flex-wrap" v-if="editable">
                                <div data-testid="ai-info-table__platform-admin-label"
                                    class="d-flex align-center mr-4">
                                    <v-icon data-testid="ai-info-table__platform-admin-icon" class="mr-2"
                                        color="primary">mdi-account-tie</v-icon>
                                    <span class="font-weight-medium">Platform Admin</span>
                                </div>

                                <div data-testid="ai-info-table__platform-admin-control">
                                    <v-switch data-testid="ai-info-table__platform-admin-switch"
                                        v-if="dialogMode === POP_MODEL.EDITOR"
                                        v-model="dialogData.platform_admin_enabled" inset color="success"
                                        :false-value="false" :true-value="true" hide-details />
                                    <v-chip data-testid="ai-info-table__platform-admin-status-chip" v-else
                                        small
                                        :color="dialogData.platform_admin_enabled === true ? 'success' : 'error'"
                                        text-color="white">
                                        <v-icon data-testid="ai-info-table__platform-admin-status-icon"
                                            left small>
                                            {{ dialogData.platform_admin_enabled === true ? 'mdi-check-circle' :
                                                'mdi-close-circle' }}
                                        </v-icon>
                                        <span>{{ dialogData.platform_admin_enabled === true ? 'Enabled' : 'Disabled' }}</span>
                                    </v-chip>
                                </div>
                            </v-col>

                            <v-col cols="12" class="">
                                <v-card data-testid="ai-info-table__super-admin-card"
                                    class="d-flex pa-3 align-center justify-space-between flex-wrap" outlined>
                                    <div data-testid="ai-info-table__super-admin-label"
                                        class="d-flex align-center mr-4">
                                        <h5 class="font-weight-regular super-admin">Super Admin</h5>
                                    </div>
                                    <div data-testid="ai-info-table__super-admin-control">
                                        <v-switch data-testid="ai-info-table__super-admin-switch"
                                            v-if="dialogMode === POP_MODEL.EDITOR"
                                            v-model="dialogData.super_admin_enabled" inset color="success"
                                            class="custom-switch" :false-value="false" :true-value="true"
                                            hide-details :disabled="true" />
                                        <v-switch v-else data-testid="notification-table__status-switch"
                                            v-model="dialogData.super_admin_enabled"
                                            inset class="mt-0 custom-switch" hide-details
                                            @change="enableDisable(dialogData)"></v-switch>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <!-- Threshold Settings -->
                <v-card data-testid="ai-info-table__threshold-settings-card" outlined class="mb-4"
                    v-if="editable">
                    <v-card-title data-testid="ai-info-table__threshold-settings-title" class="pa-4">
                        <v-icon data-testid="ai-info-table__threshold-settings-icon" left
                            small>mdi-gauge</v-icon>
                        <h5 class="font-weight-medium">Threshold Settings</h5>
                    </v-card-title>
                    <v-card-text data-testid="ai-info-table__threshold-settings-text" class="pa-4">
                        <template v-if="dialogData.threshold && typeof dialogData.threshold === 'object'">
                            <template v-if="dialogMode === POP_MODEL.EDITOR">
                                <v-row class="align-center">
                                    <!-- Total Threshold -->
                                    <v-col cols="12"
                                        class="d-flex align-center justify-space-between flex-wrap">
                                        <div data-testid="ai-info-table__total-threshold-label"
                                            class="d-flex align-center mr-4">
                                            <v-icon data-testid="ai-info-table__total-threshold-label-icon"
                                                class="mr-2" color="primary">mdi-counter</v-icon>
                                            <span class="font-weight-medium">Total Threshold</span>
                                        </div>
                                        <div data-testid="ai-info-table__total-threshold-controls"
                                            class="d-flex align-center threshold-control-container">
                                            <v-switch data-testid="ai-info-table__total-threshold-switch"
                                                v-model="dialogData.limit_check_enabled" inset
                                                color="success" :false-value="false" :true-value="true"
                                                class="threshold-switch ma-0 pa-0" />
                                            <v-text-field
                                                data-testid="ai-info-table__total-threshold-input"
                                                v-if="dialogData.limit_check_enabled"
                                                v-model.number="dialogData.threshold.total" label="Total"
                                                class="ml-3 threshold-input" dense type="number" outlined
                                                :rules="[v => !!v || 'Total is required']" required />
                                            <v-chip
                                                data-testid="ai-info-table__total-threshold-display-chip"
                                                v-else class="ml-3 threshold-chip" small color="cyan"
                                                outlined>
                                                <v-icon
                                                    data-testid="ai-info-table__total-threshold-display-icon"
                                                    left small>mdi-counter</v-icon>
                                                <span>Total: {{ dialogData.threshold.total }}</span>
                                            </v-chip>
                                        </div>
                                    </v-col>

                                    <!-- Hour Threshold -->
                                    <v-col cols="12"
                                        class="d-flex align-center justify-space-between flex-wrap">
                                        <div data-testid="ai-info-table__hour-threshold-label"
                                            class="d-flex align-center mr-4">
                                            <v-icon data-testid="ai-info-table__hour-threshold-label-icon"
                                                class="mr-2" color="primary">mdi-clock-outline</v-icon>
                                            <span class="font-weight-medium">Hour Threshold</span>
                                        </div>
                                        <div data-testid="ai-info-table__hour-threshold-controls"
                                            class="d-flex align-center threshold-control-container">
                                            <v-switch data-testid="ai-info-table__hour-threshold-switch"
                                                v-model="dialogData.hour_check_enabled" inset
                                                color="success" :false-value="false" :true-value="true"
                                                class="threshold-switch ma-0 pa-0" />
                                            <v-text-field
                                                data-testid="ai-info-table__hour-threshold-input"
                                                v-if="dialogData.hour_check_enabled"
                                                v-model.number="dialogData.threshold.hour" label="Per Hour"
                                                class="ml-3 threshold-input" dense type="number" outlined
                                                :rules="[v => !!v || 'Per Hour is required']" required />
                                            <v-chip
                                                data-testid="ai-info-table__hour-threshold-display-chip"
                                                v-else class="ml-3 threshold-chip" small color="light-green"
                                                outlined>
                                                <v-icon
                                                    data-testid="ai-info-table__hour-threshold-display-icon"
                                                    left small>mdi-clock-outline</v-icon>
                                                <span>Per Hour: {{ dialogData.threshold.hour }}</span>
                                            </v-chip>
                                        </div>
                                    </v-col>

                                    <!-- Minute Threshold -->
                                    <v-col cols="12"
                                        class="d-flex align-center justify-space-between flex-wrap">
                                        <div data-testid="ai-info-table__minute-threshold-label"
                                            class="d-flex align-center mr-4">
                                            <v-icon
                                                data-testid="ai-info-table__minute-threshold-label-icon"
                                                class="mr-2" color="primary">mdi-timer-outline</v-icon>
                                            <span class="font-weight-medium">Minute Threshold</span>
                                        </div>
                                        <div data-testid="ai-info-table__minute-threshold-controls"
                                            class="d-flex align-center threshold-control-container">
                                            <v-switch
                                                data-testid="ai-info-table__minute-threshold-switch"
                                                v-model="dialogData.minute_check_enabled" inset
                                                color="success" :false-value="false" :true-value="true"
                                                class="threshold-switch ma-0 pa-0" />
                                            <v-text-field
                                                data-testid="ai-info-table__minute-threshold-input"
                                                v-if="dialogData.minute_check_enabled"
                                                v-model.number="dialogData.threshold.minute"
                                                label="Per Minute" class="ml-3 threshold-input" dense
                                                type="number" outlined
                                                :rules="[v => !!v || 'Per Minute is required']" required />
                                            <v-chip
                                                data-testid="ai-info-table__minute-threshold-display-chip"
                                                v-else class="ml-3 threshold-chip" small color="indigo"
                                                outlined>
                                                <v-icon
                                                    data-testid="ai-info-table__minute-threshold-display-icon"
                                                    left small>mdi-timer-outline</v-icon>
                                                <span>Per Minute: {{ dialogData.threshold.minute }}</span>
                                            </v-chip>
                                        </div>
                                    </v-col>
                                </v-row>
                            </template>
                            <template v-else>
                                <v-chip data-testid="ai-info-table__readonly-total-threshold-chip"
                                    class="ma-1" small color="cyan" outlined>
                                    <v-icon data-testid="ai-info-table__readonly-total-threshold-icon"
                                        left small>mdi-counter</v-icon>
                                    <span>Total: {{ dialogData.threshold.total }}</span>
                                </v-chip>
                                <v-chip data-testid="ai-info-table__readonly-hour-threshold-chip"
                                    class="ma-1" small color="light-green" outlined>
                                    <v-icon data-testid="ai-info-table__readonly-hour-threshold-icon"
                                        left small>mdi-clock-outline</v-icon>
                                    <span>Per Hour: {{ dialogData.threshold.hour }}</span>
                                </v-chip>
                                <v-chip data-testid="ai-info-table__readonly-minute-threshold-chip"
                                    class="ma-1" small color="indigo" outlined>
                                    <v-icon data-testid="ai-info-table__readonly-minute-threshold-icon"
                                        left small>mdi-timer-outline</v-icon>
                                    <span>Per Minute: {{ dialogData.threshold.minute }}</span>
                                </v-chip>
                            </template>
                        </template>
                    </v-card-text>
                </v-card>

                <!-- Input Configuration (schema-driven prompt assembly) -->
                <AiInputSchemaEditor
                    ref="schemaEditor"
                    :feature-name="dialogData.name"
                    :editable="schemaEditorEditable"
                    :allow-static-overrides="allowStaticOverrides"
                    :feature-data="dialogData"
                    :client-id="clientId"
                    :client-code="clientCode"
                    @toggle-schema="onSchemaToggle"
                    @static-overrides-change="onStaticOverridesChange"
                />

                <!-- Prompt Variants (View Mode Only) -->
                <v-card data-testid="ai-info-table__prompt-variants-card" outlined
                    v-if="dialogMode !== POP_MODEL.EDITOR && editable">
                    <v-card-title data-testid="ai-info-table__prompt-variants-title" class="pa-4">
                        <v-icon data-testid="ai-info-table__prompt-variants-title-icon" left
                            small>mdi-text-box-multiple</v-icon>
                        <h5 class="font-weight-medium">Prompt Variants</h5>
                    </v-card-title>
                    <v-card-text data-testid="ai-info-table__prompt-variants-text" class="pa-4">
                        <div data-testid="ai-info-table__prompt-variants-content"
                            v-if="Array.isArray(dialogData.prompt_variants_data) && dialogData.prompt_variants_data.length">
                            <v-expansion-panels data-testid="ai-info-table__prompt-variants-panels"
                                accordion>
                                <v-expansion-panel data-testid="ai-info-table__prompt-variant-panel"
                                    v-for="(variant, i) in dialogData.prompt_variants_data" :key="i">
                                    <v-expansion-panel-header
                                        data-testid="ai-info-table__prompt-variant-header"
                                        class="font-weight-medium">
                                        {{ variant.type }}
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content
                                        data-testid="ai-info-table__prompt-variant-content">
                                        <v-row v-if="variant.content">
                                            <v-col cols="12">
                                                <v-card data-testid="ai-info-table__prompt-content-card"
                                                    outlined class="mt-2">
                                                    <v-card-title
                                                        data-testid="ai-info-table__prompt-content-title"
                                                        class="pa-3 grey lighten-3">
                                                        Prompt Content
                                                    </v-card-title>
                                                    <v-card-text
                                                        data-testid="ai-info-table__prompt-content-text"
                                                        class="pa-3">
                                                        <pre data-testid="ai-info-table__prompt-content-pre"
                                                            class="text-body-2 ma-0 prompt-content prompt-content-display">
                                                            {{ variant.content }}
                                                        </pre>
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>
                        <div data-testid="ai-info-table__no-prompt-variants" v-else
                            class="text-center py-4 grey--text">
                            <v-icon data-testid="ai-info-table__no-prompt-variants-icon"
                                class="mb-2">mdi-information-outline</v-icon>
                            <div>No prompt variants available</div>
                        </div>
                    </v-card-text>
                </v-card>
                <!-- Prompt List (View Mode Only) -->
                <v-card outlined class="prompt-card d-flex flex-column gap-3 pa-3" v-if="!editable">
                    <v-card-title data-testid="ai-info-table__admin-permissions-title" class="pa-1">
                        <h5 class="font-weight-semibold">Prompt List</h5>
                    </v-card-title>
                    <div v-for="(promptItem, pIndex) in filteredItems" :key="promptItem._id" class="prompt-list">
                        <v-card-title class="section-title pa-0">
                            <h5 class="font-weight-semibold text-capitalize">
                                {{ promptItem.type }} List
                            </h5>
                        </v-card-title>

                        <v-card-text class="pa-0">

                        <v-radio-group
                            v-model="selectedPrompt[promptItem.type]"
                            class="ma-0"
                            hide-details
                        >
                            <v-expansion-panels accordion flat>

                            <v-expansion-panel
                                v-for="(prompt, index) in promptsByType[promptItem.type]"
                                :key="prompt._id"
                                :class="[
                                'prompt-panel',
                                selectedPrompt[promptItem.type] === prompt._id
                                    ? 'prompt-panel--active'
                                    : ''
                                ]"
                            >
                                <!-- HEADER -->
                                <v-expansion-panel-header class="prompt-header pa-4">
                                    <div class="d-flex align-center w-100">
                                        <v-radio
                                        :value="prompt._id"
                                        class="mr-3 ma-0"
                                        @click.native.stop
                                        />
                                        <span class="font-weight-medium">
                                        {{ promptItem.type }} {{ index + 1 }}
                                        </span>
                                    </div>
                                </v-expansion-panel-header>
                                <v-divider />
                                <!-- CONTENT -->
                                <v-expansion-panel-content>
                                <div class="prompt-content">
                                    <pre class="ma-0 pre-wrap">
                                        {{ prompt.content }}
                                    </pre>
                                </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                            </v-expansion-panels>
                        </v-radio-group>
                        </v-card-text>
                    </div>
                </v-card>
            </v-container>
        </template>

        <template v-slot:actions>
            <v-spacer></v-spacer>
            <v-btn data-testid="ai-info-table__save-edit-button" v-if="dialogMode === POP_MODEL.EDITOR"
                color="primary" @click="saveEdit(dialogData)" depressed>
                <p>Save</p>
            </v-btn>
            <v-btn data-testid="ai-info-table__save-changes-button" v-if="!editable" color="primary"
                @click="saveChanges(dialogData, selectedPrompt)" depressed>
                <p>Save Changes</p>
            </v-btn>
            <v-btn class="secondaryBtn" data-testid="ai-info-table__close-button" @click="internalShow = false"
                depressed>
                <p>{{ dialogMode === POP_MODEL.EDITOR ? 'Cancel' : 'Close' }}</p>
            </v-btn>
        </template>
    </lb-dialog>
</template>

<script>
import { POP_MODEL, MESSAGES } from '@/modules/admin/utils/constants.js';
import { updateFeatureById, updateFeatureByClient } from '@/modules/varianceanalysis/aiUtils/summary.js';
import AiInputSchemaEditor from './AiInputSchemaEditor.vue';

export default {
    name: 'AiFeatureDialog',
    components: {
        AiInputSchemaEditor,
    },
    props: {
        value: {
            type: Boolean,
            required: true
        },
        dialogData: {
            type: Object,
            required: true
        },
        dialogMode: {
            type: String,
            required: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        clientId: {
            type: String,
            required: true
        },
        clientCode: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            POP_MODEL,
            expandedPanel: null,
            selectedPrompt: {
                instruction: null,
                output: null
            },
            staticInputOverrides: [],
        };
    },
    computed: {
        dialogHeading() {
            return this.dialogMode === POP_MODEL.EDITOR ? 'Edit Feature' : 'Feature Details';
        },
        dialogWidth() {
            return this.schemaEditorEditable || this.dialogData?.use_input_schema ? '85vw' : '60vw';
        },
        schemaEditorEditable() {
            return this.editable && this.dialogMode === POP_MODEL.EDITOR;
        },
        allowStaticOverrides() {
            return !this.editable;
        },
        internalShow: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        filteredItems() {
            if (!this.dialogData || !this.dialogData.prompt_variants_data || !Array.isArray(this.dialogData.prompt_variants_data)) {
                return [];
            }
            return this.dialogData.prompt_variants_data.filter(
                item => item.type === "instruction" || item.type === "output"
            );
        },
        promptsByType() {
            return this.$store.getters['admin/getPromptsByType'];
        }
    },
    watch: {
        dialogData: {
             handler(newVal) {
                if (newVal && newVal.prompt_variants_data) {
                    this.initSelectedPrompt();
                    this.expandedPanel = null;
                    this.$store.commit('admin/clearPromptsByType');
                    this.fetchAllPromptLists();
                }
            },
            deep: true,
            immediate: true
        },
        promptsByType: {
            handler() {
               Object.keys(this.promptsByType || {}).forEach(t => {
                   // When prompts load, ensure we select the current one if it exists, or fallback to first
                    const item = { _id: this.selectedPrompt[t], type: t };
                    this.ensureSelection(t, item);
                });
            },
            deep: true
        }
    },
    beforeDestroy() {
        this.$store.commit('admin/clearInputSchema');
    },
    methods: {
        initSelectedPrompt() {
             const map = {};
            (this.dialogData.prompt_variants_data || []).forEach(item => {
                if (item && item.type && item._id) map[item.type] = item._id;
            });
            this.selectedPrompt = map;
        },
         getSelectedPrompt(type) {
            return this.selectedPrompt[type] || null;
        },
        setSelectedPrompt(type, val) {
            if (typeof val === 'undefined') return;
            if (!(type in this.selectedPrompt)) {
              this.$set(this.selectedPrompt, type, val);
            } else {
              this.selectedPrompt[type] = val;
            }
        },
        ensureSelection(type, promptItem) {
            const list = (this.promptsByType || {})[type] || [];
            if (!list.length) return;
            const match = list.find(prompt => prompt._id === promptItem._id);
            if (match) {
              this.setSelectedPrompt(type, match._id);
            } else if (!this.getSelectedPrompt(type)) {
              this.setSelectedPrompt(type, list[0]._id);
            }
        },
        async fetchAllPromptLists() {
            if (!this.filteredItems) return;
            for (const item of this.filteredItems) {
                try {
                    await this.$store.dispatch('admin/fetchPromptList', {
                        axiosInstance: this.axios,
                        feature: item.feature,
                        type: item.type,
                        promptId: item._id
                    });
                } catch (error) {
                    console.error('Error fetching prompt list:', error);
                }
            }
        },
        async onPanelClick(promptItem) {
            try {
                await this.$store.dispatch('admin/fetchPromptList', {
                    axiosInstance: this.axios,
                    feature: promptItem.feature,
                    type: promptItem.type,
                    promptId: promptItem._id
                });
                this.ensureSelection(promptItem.type, promptItem);
            } catch (error) {
                console.error('Error fetching prompt list:', error);
                this.$store.commit('sbError', error.message || error || 'Unknown error!');
            }
        },
        onSchemaToggle(enabled) {
            this.$set(this.dialogData, 'use_input_schema', enabled);
        },
        onStaticOverridesChange(overrides) {
            this.staticInputOverrides = Array.isArray(overrides) ? overrides : [];
        },
        async persistInputSchemaIfNeeded() {
            if (!this.dialogData?.use_input_schema || !this.$refs.schemaEditor) {
                return true;
            }
            const saved = await this.$refs.schemaEditor.saveSchema();
            return saved !== false;
        },
        async enableDisable(item) {
            try {
                // Toggle the super admin flag
                // item.super_admin_enabled = !item.super_admin_enabled;

                const updatePayload = {
                    id: item._id,
                    limit_check_enabled: item.limit_check_enabled,
                    hour_check_enabled: item.hour_check_enabled,
                    minute_check_enabled: item.minute_check_enabled,
                    platform_admin_enabled: item.platform_admin_enabled,
                    super_admin_enabled: item.super_admin_enabled,
                    threshold: item.threshold,
                    client: this.clientId
                };
                
                const response = await this.$store.dispatch('varianceanalysis/updateAiFeatureByClient', {
                    axiosInstance: this.axios,
                    data: updatePayload
                });

                if (response?.data.status === 'success') {
                    this.$toast?.success(MESSAGES.SUCCESS.STATUS_CHANGED);
                    this.$emit('refresh');
                } else {
                    item.super_admin_enabled = !item.super_admin_enabled;
                    const msg = response?.message || MESSAGES.ERROR.FAILED_UPDATE_FEATURE;
                    this.$toast?.error(msg);
                }
            } catch (err) {
                item.super_admin_enabled = !item.super_admin_enabled;
                 const msg = err?.message || MESSAGES.ERROR.FAILED_UPDATE_FEATURE;
                this.$toast?.error(msg);
                console.error('API Error:', err);
            }
        },
        async saveChanges(items, selectedPrompt) {
            const updatedPromptVariants = items.prompt_variants_data.map(item => {
                const newId = selectedPrompt[item.type];
                return newId ? { ...item, _id: newId } : item;
            });
            
            const prompt_variants = updatedPromptVariants.map(item => item._id);
            try {
                const updatePayload = {
                    id: items._id,
                    client: this.clientId,
                    code: this.clientCode,
                    prompt_variants: prompt_variants,
                    static_input_overrides: this.staticInputOverrides
                };
                // Platform admin edits target client ai_feature_center (not session tenant)
                const response = this.editable && this.clientCode
                    ? await updateFeatureById(this.axios, updatePayload)
                    : await updateFeatureByClient(this.axios, updatePayload);
                const body = response?.data || response;
                if (body && body.status === 'success') {
                    this.$toast?.success(MESSAGES.SUCCESS.STATUS_CHANGED);
                } else {
                    const msg = body?.message || MESSAGES.ERROR.FAILED_UPDATE_FEATURE;
                    this.$toast?.error(msg);
                }
            } catch (err) {
                const msg = err?.message || MESSAGES.ERROR.FAILED_UPDATE_FEATURE;
                this.$toast?.error(msg);
            } finally {
                this.internalShow = false;
                this.$emit('refresh');
            }
        },
        async saveEdit(dialogData) {
            try {
                const schemaPayload = this.$refs.schemaEditor?.getSchemaPayload?.() || {};
                const useInputSchema = schemaPayload.use_input_schema ?? !!dialogData.use_input_schema;
                this.$set(dialogData, 'use_input_schema', useInputSchema);

                const updatePayload = {
                    id: dialogData._id,
                    limit_check_enabled: dialogData.limit_check_enabled,
                    hour_check_enabled: dialogData.hour_check_enabled,
                    minute_check_enabled: dialogData.minute_check_enabled,
                    platform_admin_enabled: dialogData.platform_admin_enabled,
                    super_admin_enabled: dialogData.super_admin_enabled,
                    threshold: dialogData.threshold,
                    use_input_schema: !!useInputSchema,
                    code: this.clientCode,
                    client: this.clientId,
                };
                const response = await updateFeatureById(this.axios, updatePayload);
                const body = response?.data || response;
                if (body?.status !== 'success') {
                    const msg = body?.message || MESSAGES.ERROR.FAILED_UPDATE_FEATURE;
                    this.$toast?.error(msg);
                    return;
                }

                const schemaSaved = await this.persistInputSchemaIfNeeded();
                if (!schemaSaved) {
                    return;
                }

                this.$toast?.success(MESSAGES.SUCCESS.STATUS_CHANGED);
                this.internalShow = false;
                this.$emit('refresh');
            } catch (err) {
                const msg = err?.message || MESSAGES.ERROR.FAILED_UPDATE_FEATURE;
                this.$toast?.error(msg);
            }
        },
    }
}
</script>

<style scoped>
.pre-wrap {
    white-space: pre-wrap;
    word-break: break-word;
}

.prompt-content {
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.87);
}

/* Prompt content display styles */
.prompt-content-display {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
}


/* Threshold control styles */
.threshold-control-container {
    min-height: 40px;
}

.threshold-switch {
    min-width: 100px;
}

.threshold-input {
    max-width: 120px;
    min-height: 40px;
}

.threshold-chip {
    min-height: 32px;
}

/* Clickable chip styles */
.clickable-chip {
    cursor: pointer;
}
.super-admin {
    color: var(--grey-900);
}
.v-expansion-panel::before{
    box-shadow: var(--shadow-none) !important;
}
::v-deep(.v-expansion-panel-content__wrap){
    padding: 0px !important;
}
/* Card container */
.prompt-card {
  border-radius: 8px;
}

/* Section header */
.section-title {
  border-bottom: 1px solid #e0e0e0;
}

/* Each instruction/output box */
.prompt-panel {
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s ease;
}

/* Selected (green border like image) */
.prompt-panel--active {
  border: 1px solid #4caf50;
}

/* Header styling */
.prompt-header {
  background: #ffffff;
  min-height: 56px;

  .v-expansion-panel-header__icon {
    color: #757575;
  }
}

/* Content block */
.prompt-content {
  padding: 16px;
  border-radius: 6px;
  font-size: 14px;
}

/* Pre text formatting */
.pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
}

.prompt-list{
    border-radius: 8px;
    border: 1px solid var(--grey-100, #E5E5E5);
    padding: 12px;
}
</style>